<template>
  <div ref="baseText"></div>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions, type PropType } from 'vue';
import messages from './validation/messages';
import { mdiAlertCircleOutline, mdiCheckCircleOutline } from '@mdi/js';

const componentOptions: ComponentOptions = {
  data: () =>
    ({
      masked: false,
      textValue: '',
      describers: '',
      maskTimeout: {},
    }) as { [key: string]: unknown },
  props: {
    modelName: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [String, Number, Object] as PropType<unknown>,
      required: true,
    },
    hint: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    formInput: {
      type: Object as PropType<unknown>,
      required: true,
    },
    enableMasking: {
      type: Boolean,
      default: false,
    },
    enableShowHide: {
      type: Boolean,
      default: false,
    },
    autoCapitalize: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: Object,
      default: () => ({}),
    },
    validationMessages: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    formatters: {
      type: [Array, Function],
      required: false,
      default: null,
    },
    messages: {
      type: [String, Array],
      required: false,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    ariaDescribedBy: {
      type: String,
      required: false,
      default: '',
    },
    disableAutocomplete: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    maskCallback() {
      this[this.modelName] = this[this.modelName].replace(/./g, '•');
    },
    onInput() {
      this.onChange(this.formInput.$model);
    },
    onBlur(event: Event) {
      this.onChange(this.formInput.$model);
      this.$emit('blur', event);
    },
    onChange(event: Event) {
      this.$emit('input', event);
    },
    onFocus(event: Event) {
      this.$emit('focus', event);
    },
    toggleTooltip(event: Event) {
      this.$emit('toggle-tooltip', event);
    },
    toggleMasked() {
      this.masked = !this.masked;
      this[this.modelName] = !this.masked
        ? this.formInput.$model
        : this.formInput.$model.replace(/./g, '•');
    },
    mask(input: string) {
      try {
        let value = input;
        if (value.length > 9) {
          value = value.slice(0, 9);
        }
        if (!this.masked) {
          this.textValue = value;
          this[this.modelName] = value;
          this.onInput(value);
          return;
        }

        const chars = value.split('');
        const currentValue = this.textValue;
        const caretPosition = this.$el.querySelector('input').selectionStart;
        this[this.modelName] = chars
          .map((char, i) => (char === currentValue[i] || i >= caretPosition ? '•' : char))
          .join('')
          .slice(0, 9);

        this.textValue = value.slice(0, 9);
        if (this.maskTimeout[this._uid]) {
          window.clearTimeout(this.maskTimeout[this._uid]);
          this.maskTimeout[this._uid] = null;
        }
        this.maskTimeout[this._uid] = window.setTimeout(() => this.maskCallback(), 1200);
        this.onInput(this.formInput.$model);
      } catch (_error) {
        /* empty */
      }
    },
    format(input: unknown) {
      if (typeof this.formatters === 'function') {
        return this.formatters(input);
      }
      let output = input;
      this.formatters.forEach((formatter: (input: unknown) => unknown) => {
        output = formatter(output);
      });
      return output;
    },
    onFormReset(message: { data: string }) {
      if (message.data === 'form-reset') {
        this.mask('');
      }
    },
    // sendGTMValidationError() {
    //   if (typeof (window.dataLayer?.push) === 'function') {
    //     window.dataLayer.push({
    //       event: 'event',
    //       eventCategory: 'form validation',
    //       eventLabel: 'error',
    //       eventValue: this.errorMessageValue,
    //       // timestamp: moment().utc().format('YYYY-MM-DDTHH:mm:ss'),
    //       sessionId: Session.get('X-Correlation-ID'),
    //     });
    //   }
    // },
  },
  computed: {
    icon() {
      if (!this.formInput.$dirty) {
        return '';
      }
      return this.formInput.$invalid ? mdiAlertCircleOutline : mdiCheckCircleOutline;
    },
    getClass() {
      return {
        'success--text': !this.formInput.$invalid,
      };
    },
    color() {
      return this.formInput.$invalid || this.formInput.$model?.length === 0 ? 'primary' : 'success';
    },
    errorMessages() {
      const errors: string[] = [];
      const validationMessages = Object.assign(
        {},
        messages[this.modelName],
        this.validationMessages
      );
      if (typeof this.formInput === 'undefined' || !this.formInput.$dirty) {
        return errors;
      }
      if (this.formInput.$error) {
        // If there is an error on the field
        Object.keys(validationMessages).forEach((type) => {
          if (
            this.formInput[type]?.$response === false &&
            validationMessages &&
            Object.prototype.hasOwnProperty.call(validationMessages, type)
          ) {
            errors.push(validationMessages[type]);
          }
        });
        if (errors.length === 0) {
          // If no messages have been added for this error
          errors.push(messages.generic.invalid);
        }
      }
      return errors.map((msg) => (typeof this.$t !== 'undefined' ? this.$t(msg) : msg));
    },
    errorMessageValue() {
      if (this.messages) {
        return this.messages;
      }
      return this.errorMessages[0].length >= 1 ? this.errorMessages[0] : this.label;
    },
  },
  mounted() {
    window.addEventListener('message', () => this.onFormReset());
    this.textValue = this.formInput.$model;
    this.masked = this.enableMasking;
    try {
      const message = this.$el.querySelector('.v-messages__wrapper');
      if (message) {
        const describerId = `${this.modelName}_error_message`;
        message.setAttribute('id', describerId);
        this.describers = `${this.ariaDescribedBy} ${describerId}`;
      }
    } catch (_error) {
      /* empty */
    }
  },
  updated() {
    if (
      this.enableMasking &&
      this[this.modelName].length === 0 &&
      this.formInput.$model.length > 0
    ) {
      this[this.modelName] = this.masked
        ? this.formInput.$model.replace(/./g, '•')
        : this.formInput.$model;
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
:deep(.v-input.masked input) {
  z-index: 1;
  color: transparent !important;
  &::selection {
    color: transparent !important;
  }
}
:deep(.v-input.masked.mask-active input) {
  font-family: monospace;
}

:deep(i.toggle-masking) {
  color: #02579e !important;
}
span.mask-text.absolute {
  position: absolute;
  padding-top: 4px;
  z-index: 0;
}
</style>
